import React, { useContext, useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Heading,
  Select,
  Button,
  RoundButton,
  CardNotification,
  Text,
} from "@foris/avocado-suite";
import { BookingContext } from "../../../context/BookingContext";
import { Types } from "../../../context/search.reducer";
import { Types as RequestTypes } from "../../../context/request.reducer";
import { Types as SavedRequestTypes } from "../../../context/savedRequest.reducer";
import { DayRowData } from "@modules/booking/context/types";
import { ClassroomBooking, Holiday } from "@models/ISchema";
import cx from "classnames";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import SingleEvent from "./SingleEvent/SingleEvent";
import RecurrentEvent from "./RecurrentEvent/RecurrentEvent";
import {
  getIsCurrentWeek,
  getSingleEventSessions,
  getUniqueRecurrentSessions,
} from "@modules/booking/utils/classroomEvents";
import { useGetHolidays } from "@services/collections/holidays/holidaysServices";
import { ContextEDH } from "@context/ContextEDH";
import { Header } from "@common/components";
import { useTranslation } from "react-i18next";
import useBookingContextUrl from "@modules/booking/hooks/useBookingContextUrl";
import useGetIntervals from "@modules/booking/hooks/useIntervals";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import css from "./search.module.scss";

dayjs.extend(isBetween);

const RECURRENCE_DEFAULT = {
  index: 1,
  recurrence: false,
  day: {
    dateValue: null,
    value: null,
    error: false,
  },
  start: {
    value: null,
    error: false,
  },
  end: {
    value: null,
    error: false,
  },
};

const getSelectorOption = (option: string, description): any => {
  return (
    <div className={css.selectorOption}>
      <span className={css.selectorOption_title}>{option}</span>
      <span className={css.selectorOption_description}>{description}</span>
    </div>
  );
};

const SearchClassRoom = () => {
  const history = useHistory();
  const {
    scenario: scenarioId,
    contextUrl,
    id: bookingId,
    isAdminBooking,
    scenarioDetails,
  } = useBookingContextUrl();
  const [{ loading: isLoadingIntervals }, getIntervals] = useGetIntervals();
  const { adaptedHomeUrl } = useNavigationUrl();
  const editing = Boolean(bookingId);
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const options = useMemo(() => {
    return [
      {
        label: getSelectorOption(
          t("booking.booking-form.type-selector.options.single"),
          t("booking.booking-form.type-selector.options.single-description"),
        ),
        value: "single_event",
      },
      {
        label: getSelectorOption(
          t("booking.booking-form.type-selector.options.recurring"),
          t("booking.booking-form.type-selector.options.recurring-description"),
        ),
        value: "recurrent_event",
      },
    ];
  }, [language]);

  const [{ holidays, isLoading: isLoadingHolidays }, getHolidays] = useGetHolidays({
    scenario: scenarioId,
  });
  const { state, dispatch } = useContext(BookingContext);
  const savedRecurrence = state?.savedRequest?.editedBooking?.isRecurrent;
  const {
    state: { base },
  } = useContext(ContextEDH);
  const [optionValue, setOptionValue] = useState(
    editing || ![null, undefined].includes(savedRecurrence)
      ? (savedRecurrence ?? state?.search?.currentBooking?.isRecurrent)
        ? options[1]
        : options[0]
      : null,
  );
  const [hasEventCollissions, setHasEventCollisions] = useState(false);
  const [shouldShowSelectors, setShouldShowSelectors] = useState(false);
  const [isOutsideRange, setIsOutsideRange] = useState(false);

  const booking = state?.search?.currentBooking;
  const listUrl = `/booking/list/${contextUrl}${!isAdminBooking ? `?campus=${scenarioId}` : ""}`;
  const editionUrl = `/booking/detail/${contextUrl}/${bookingId}`;

  if (!booking && editing) {
    history.replace(editionUrl);
  }

  const handleClearState = () => {
    dispatch({ type: Types.ClearSearch, payload: null });
    dispatch({ type: RequestTypes.ClearRequest, payload: null });
    dispatch({ type: SavedRequestTypes.clearSavedData, payload: null });
  };

  const dataValidator = () => {
    let peopleValidator = true;
    let dateTimeValidator = true;
    let programValidator = true;

    if (!state?.search?.requirements?.people?.count) {
      const cloneRequirements = { ...state?.search?.requirements };
      cloneRequirements.people.error = true;
      dispatch({ type: Types.SetRequirements, payload: cloneRequirements });
      peopleValidator = false;
    }

    const cloneDateTime = [];
    let setDate = false;
    state?.search?.dateTime?.forEach(value => {
      const { day, start, end } = value;
      const cloneItem = { ...value };
      if (!day.value) cloneItem.day.error = true;
      if (!start.value) cloneItem.start.error = true;
      if (!end.value) cloneItem.end.error = true;
      if ((!day.value || !start.value || !end.value) && !setDate) {
        setDate = true;
      }
      cloneDateTime.push(cloneItem);
    });

    if (setDate) {
      dispatch({ type: Types.SetDateTime, payload: cloneDateTime });
      dateTimeValidator = false;
    }

    const program = state?.search?.preferences?.program?.value;
    if (base?.bookingsProgramRequired === "REQUIRED" && !program) {
      const clonePreferences = { ...state?.search?.preferences };
      clonePreferences.program = {
        label: null,
        value: null,
        error: true,
      };

      dispatch({ type: Types.SetPreferences, payload: clonePreferences });
      programValidator = false;
    }

    return peopleValidator && dateTimeValidator && programValidator;
  };

  const calculateSessions = (newEditedBooking: ClassroomBooking) => {
    // sometimes the state?.search?.dateTimes' `value` are an actual date.
    // We have to consider it to build the recurrent sessions correctly
    const getDayId = (dayValue: any) => {
      const isNumeric = (value: string) => /^-?\d+$/.test(value);
      if (isNumeric(dayValue)) return dayValue;
      const dayId = dayjs(dayValue).day();
      return dayId === 0 ? "7" : dayId.toString();
    };
    const formatDate = (date: Date, time: string) => {
      const year = date.getFullYear();
      const month = ("0" + (date.getMonth() + 1)).slice(-2);
      const day = ("0" + date.getDate()).slice(-2);
      return `${year}-${month}-${day} ${time}:00`;
    };
    const isSameDay = (date: Date, dayId: string) => {
      const dateDayId = date.getDay().toString();
      return dateDayId === "0" ? dayId === "7" : dateDayId === dayId;
    };

    const updatedDates = [];

    if (newEditedBooking?.isRecurrent) {
      const selectedWeeks = (state?.search?.intervals ?? []).filter(week => week?.selected);
      const events = (state?.request?.editedBooking?.sessions ?? [])[0]?.events;
      const dates = state?.search?.dateTime;

      const sessions = [];
      selectedWeeks?.forEach(week => {
        const start = new Date(week?.startingDate);
        const end = new Date(week?.endingDate);

        dates.forEach(date => {
          const dayId = getDayId(date?.day?.value);
          const currDate = new Date(start?.getTime());

          if (!dayId || !currDate) return;

          // move forward one day at a time until we reach the target day
          while (!isSameDay(currDate, dayId)) {
            if (isNaN(dayId)) break;
            currDate.setDate(currDate.getDate() + 1);
          }

          // keep adding one session per week for the defined day/start/end
          // until the `end` date is reached
          while (currDate <= end) {
            const start = formatDate(currDate, date?.start?.value?.value);
            const end = formatDate(currDate, date?.end?.value?.value);
            const formattedStart = start?.split(" ")[0];

            updatedDates.push({
              ...date,
              day: {
                ...date?.day,
                dateValue: formattedStart,
                isHoliday: formattedStart in holidays,
              },
            });

            sessions.push({
              events,
              scheduleEvent: [
                {
                  start,
                  end,
                },
              ],
            });
            // move a week forward
            currDate?.setDate(currDate?.getDate() + 7);
          }
        });
      });

      if (updatedDates?.length) {
        dispatch({ type: Types.SetDateTimeFormatted, payload: updatedDates });
      }

      return sessions;
    } else {
      const dates = state?.search?.dateTime ?? [];
      const events = (state?.search?.currentBooking?.sessions ?? [])[0]?.events;
      const sessions = dates.map((date: any) => {
        updatedDates.push({
          ...date,
          day: {
            ...date?.day,
            dateValue: date?.day?.value,
            isHoliday: date?.day?.value in holidays,
          },
        });

        return {
          events,
          scheduleEvent: [
            {
              start: `${date?.day?.value} ${date?.start?.value?.value}:00`,
              end: `${date?.day?.value} ${date?.end?.value?.value}:00`,
            },
          ],
        };
      });

      if (updatedDates?.length) {
        dispatch({ type: Types.SetDateTimeFormatted, payload: updatedDates });
      }

      return sessions;
    }
  };

  const handleUpdateRecurrence = (option: { label: string; value: string }) => {
    const defaultObj = { ...RECURRENCE_DEFAULT };

    !editing && handleClearState();
    setOptionValue(option);
    dispatch({
      type: Types.SetRecurrence,
      payload: {
        recurrence: option?.value === "recurrent_event",
        shouldResetDateTime: !editing,
      },
    });

    if (optionValue?.value === "single_event" && state?.search?.recurrence && !editing) {
      defaultObj.day.value = dayjs(new Date()).format("YYYY-MM-DD").toString();
      dispatch({ type: Types.SetDateTime, payload: [defaultObj] });
    }
    if (optionValue?.value === "recurrent_event" && !state?.search?.recurrence && !editing) {
      defaultObj.day.value = "1";
      defaultObj.recurrence = true;
      dispatch({ type: Types.SetDateTime, payload: [defaultObj] });
    }
  };

  useEffect(() => {
    if (!Object.keys(holidays).length && !isLoadingHolidays) {
      getHolidays();
    }

    if (state?.search?.intervals === null) {
      getIntervals(scenarioId);
    }

    dispatch({
      type: Types.SetRecurrence,
      payload: {
        recurrence: optionValue?.value === "recurrent_event",
        shouldResetDateTime: !editing,
      },
    });
  }, []);

  useEffect(() => {
    const booking = state?.savedRequest?.editedBooking ?? state?.search?.currentBooking;

    if (booking) {
      const sessions = [...(booking?.sessions || [])];
      const parser =
        optionValue?.value === "single_event" ? getSingleEventSessions : getUniqueRecurrentSessions;
      const datetime = parser(sessions);

      dispatch({ type: Types.SetDateTime, payload: datetime as DayRowData[] });
    }

    setShouldShowSelectors(true);
  }, [state?.search?.currentBooking]);

  useEffect(() => {
    const newEditedBooking = {
      ...(state?.request?.editedBooking ?? {}),
      isRecurrent: Boolean(state?.search?.recurrence),
      sessions: calculateSessions(state?.request?.editedBooking),
      capacity: state?.search?.requirements?.people?.count,
    } as ClassroomBooking;
    dispatch({ type: RequestTypes.SetEditedBooking, payload: newEditedBooking });
  }, [
    state?.search?.recurrence,
    state?.search?.dateTime,
    state?.search?.requirements?.people?.count,
    state?.search?.intervals,
  ]);

  const handleSearchClassroom = () => {
    if (!dataValidator()) return;

    dispatch({
      type: SavedRequestTypes.setSaveEditedBooking,
      payload: state?.request?.editedBooking,
    });
    dispatch({
      type: SavedRequestTypes.setSavedBookingCapacity,
      payload: state?.search?.requirements?.people?.count,
    });
    dispatch({
      type: SavedRequestTypes.setSavedBookingPreferences,
      payload: state?.search?.preferences,
    });
    dispatch({
      type: SavedRequestTypes.setSavedRecurrence,
      payload: state?.search?.recurrence,
    });
    dispatch({ type: Types.SetView, payload: "result" });
  };

  const selectedHolidays = useMemo(() => {
    const sessions = [...(state?.request?.editedBooking?.sessions || [])];
    const filteredHolidaysSessions = {};

    sessions?.forEach(session => {
      session?.scheduleEvent?.forEach(({ start, end }) => {
        const date = (start || "").split(" ")[0];

        if (date in holidays && !end.includes("undefined")) {
          filteredHolidaysSessions[date] = holidays[date];
        }
      });
    });

    return Object.values(filteredHolidaysSessions);
  }, [state?.request?.editedBooking?.sessions]);

  const handleGoBack = () => {
    handleClearState();
    history.replace(editing ? editionUrl : listUrl);
  };

  const hasAnyIntervalSelected = useMemo(() => {
    return (
      state?.search?.recurrence && state?.search?.intervals?.some(interval => interval.selected)
    );
  }, [state?.search?.intervals, state?.search?.recurrence]);

  const canSearchClassrooms = useMemo(() => {
    const dateTimes = state?.search?.dateTime ?? [];
    const allDateTimesValid = dateTimes?.every(
      ({ day, start, end }) => day?.value && start?.value?.value && end?.value?.value,
    );

    if (!dateTimes?.length || !allDateTimesValid) {
      return false;
    }

    if (state?.search?.recurrence && !hasAnyIntervalSelected) {
      return false;
    }

    if (!state?.search?.requirements?.people?.count) {
      return false;
    }

    if (
      base?.bookingsProgramRequired === "REQUIRED" &&
      !state?.search?.preferences?.program?.value
    ) {
      return false;
    }

    return true;
  }, [state?.search, base?.bookingsProgramRequired]);

  const notUsableInBookingIntervals = useMemo(() => {
    if (isAdminBooking) return [];

    return (
      [...(state?.search?.intervals ?? [])]?.filter(interval => !interval?.usableInBookings) ?? []
    );
  }, [state?.search?.intervals]);

  const someDateIsNotUsableInBooking = useMemo(() => {
    if (!notUsableInBookingIntervals?.length || !shouldShowSelectors) {
      return false;
    }

    if (optionValue?.value === "recurrent_event") {
      return notUsableInBookingIntervals.some(interval => interval?.selected);
    }

    return state?.search?.dateTime?.some(date => {
      return notUsableInBookingIntervals.some(interval => {
        return (
          !!date?.start?.value &&
          !!date?.end?.value &&
          dayjs(date?.day?.value).isBetween(
            interval?.startingDate,
            interval?.endingDate,
            "day",
            "[]",
          )
        );
      });
    });
  }, [state?.search?.dateTime, notUsableInBookingIntervals, shouldShowSelectors]);

  const scenarioIsOutsideRange = useMemo(() => {
    if (!state?.search?.intervals?.length || !shouldShowSelectors) {
      return false;
    }

    const hasEnabledWeeks = state?.search?.intervals?.some(week => {
      const { isCurrentWeek, isBeforeCurrentWeek } = getIsCurrentWeek(week);
      return isCurrentWeek || !isBeforeCurrentWeek;
    });

    return !hasEnabledWeeks;
  }, [state?.search?.intervals]);

  return (
    <>
      <Header
        breadcrumbItems={[
          { title: t("home.breadcrumb.current"), path: adaptedHomeUrl() },
          { title: t("booking.title") },
        ]}
        title={t("booking.title")}
      />

      <section className={css.classroomSearch}>
        <div className={css.classroomSearch_header}>
          <RoundButton size="sm" variant="ghost" icon="chevron-left" onClick={handleGoBack} />
          <Heading type="h2">
            {editing
              ? t("booking.booking-form.edit-title")
              : t("booking.booking-form.creation-title")}
          </Heading>
        </div>

        {!isAdminBooking && !!scenarioDetails?.name && (
          <Box className={css.classroomSearch_scenario}>
            <div className={css.scenarioContent}>
              <Text className={css.scenarioContent_title} type="sm">
                Escenario
              </Text>
              <Text className={css.scenarioContent_value}>{scenarioDetails?.name ?? "-"}</Text>
            </div>
          </Box>
        )}

        <Box className={css.classroomSearch_content}>
          <section
            className={cx(
              css.contentTypeSelector,
              optionValue?.value && css.contentTypeSelector__hasValue,
            )}
          >
            <Heading type="h4" className={css.contentTypeSelector_title}>
              {t("booking.booking-form.type-selector.title")}
            </Heading>

            <Select
              label={t("booking.booking-form.type-selector.label")}
              className={css.contentTypeSelector_selector}
              placeholder={t("booking.booking-form.type-selector.placeholder")}
              options={options}
              value={optionValue}
              isLoading={isLoadingIntervals}
              disabled={isLoadingIntervals}
              onChange={handleUpdateRecurrence}
            />
          </section>
          {optionValue?.value === "single_event" && shouldShowSelectors && (
            <SingleEvent
              selectedHolidays={selectedHolidays as Holiday[]}
              hasNotUsableDatesSelected={someDateIsNotUsableInBooking}
              onCollission={setHasEventCollisions}
              onOutsideRange={setIsOutsideRange}
            />
          )}
          {optionValue?.value === "recurrent_event" && shouldShowSelectors && (
            <RecurrentEvent
              onCollission={setHasEventCollisions}
              selectedHolidays={selectedHolidays as Holiday[]}
            />
          )}
        </Box>

        {hasEventCollissions && (
          <CardNotification
            title="Error"
            state="error"
            outlined
            className={css.classroomSearch_errorNotification}
          >
            {t("booking.booking-form.notifications.event-conflict.description")}
          </CardNotification>
        )}

        {scenarioIsOutsideRange && (
          <CardNotification
            className={css.classroomSearch_errorNotification}
            title={t("booking.booking-list-app.notifications.scenario-outside-range.title")}
            state="error"
            outlined
          >
            <Text type="sm">
              {t("booking.booking-list-app.notifications.scenario-outside-range.description")}
            </Text>
          </CardNotification>
        )}

        <footer className={css.classroomSearch_footer}>
          <Button variant="ghost" onClick={handleGoBack}>
            {t("booking.booking-form.cancel")}
          </Button>
          <Button
            disabled={
              hasEventCollissions ||
              !optionValue?.value ||
              !canSearchClassrooms ||
              (!isAdminBooking && !!selectedHolidays?.length) ||
              (!isAdminBooking && someDateIsNotUsableInBooking) ||
              scenarioIsOutsideRange ||
              isOutsideRange ||
              (optionValue?.value === "recurrent_event" && !hasAnyIntervalSelected)
            }
            onClick={handleSearchClassroom}
          >
            {t("booking.booking-form.search-classroom")}
          </Button>
        </footer>
      </section>
    </>
  );
};

export default SearchClassRoom;
